import {DateTime} from "luxon";
import type {
    ComboChartSeriesDetails,
    YAxisId,
} from "~/components/reports/widgetSpecific/comboChart";
import {generateHadesDataForAllDatesBasedOnGranularity} from "~/components/reports/widgetSpecific/queryResolver";
import type {
    ChartSeries,
    ComboChartSeries,
    EditableReportPieChartData,
    EditableReportTableData,
} from "~/components/reports/widgetSpecific/types";
import {graphColors, pieColors} from "~/constants/graphColors";
import {EratoColumnTypes, EratoDataTypes} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/a/_adminScaffold/debugger/-types";
import {Granularity} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-types";
import type {
    Iso8601DateTime,
    Uuid,
} from "@vesta/common-type-definitions/typeDefinitions";
import {
    distinct,
    generateUuid,
} from "@vesta/global-common-typescript/utilities/utilities";

export type HadesRawData = {
    columnMetadata: Array<HadesColumnMetadata>;
    dataPoints: Array<
        Array<{
            value: string | number | null;
        }>
    >;
    requestBody?: unknown;
};

export type FrontendColumnMetadata = {
    [key: string]: keyof typeof EratoDataTypes;
};

export type HadesColumnMetadata = {
    id: Uuid;
    columnIndex: number;
    displayName: string;
    dataType: keyof typeof EratoDataTypes;
    columnType: keyof typeof EratoColumnTypes;
};

// TODO: Return the proper error code from here
export function transformChartData(
    rawData: HadesRawData,
    startDate: Iso8601DateTime,
    endDate: Iso8601DateTime,
    granularity: Granularity | null = Granularity.day,
    secondaryAxisSelectedMetrics: Array<Uuid>,
): {
    chartData: Array<Record<string, string | number | null>>;
    chartSeries: Array<ChartSeries>;
    minYValue: number;
    maxYValue: number;
    dimensionName: string;
    minYValueSecondary: number;
    maxYValueSecondary: number;
    title: string;
} | null {
    let dataWithAllDates: HadesRawData = {
        columnMetadata: rawData.columnMetadata,
        dataPoints: [],
    };

    if (rawData.columnMetadata.length === 0) {
        return null;
    }

    if (rawData.dataPoints.length === 0) {
        return null;
    }

    // Validating whether column metadata count matches the number of values in each dataPoint
    const metadataLength = rawData.columnMetadata.length;
    for (const dataPoint of rawData.dataPoints) {
        if (metadataLength !== dataPoint.length) {
            return null;
        }
    }

    // TODO: Hardcoding a single dimension for charts, fix generalise later
    const dimension = rawData.columnMetadata.find(
        (metadata) => metadata.columnType === EratoColumnTypes.dimension,
    );
    if (dimension === undefined) {
        return null;
    }
    const dateColumnIndex: number | null =
        dimension.dataType === EratoDataTypes.date
            ? dimension.columnIndex
            : null;

    if (dateColumnIndex === null) {
        dataWithAllDates = rawData;
    } else {
        dataWithAllDates = generateHadesDataForAllDatesBasedOnGranularity(
            rawData,
            dateColumnIndex,
            startDate,
            endDate,
            granularity === null ? Granularity.day : granularity,
        );
    }

    const chartData: Array<Record<string, string | number | null>> = [];

    let minValue = Infinity;
    let maxValue = -Infinity;

    let minSecondaryAxisValue = Infinity;
    let maxSecondaryAxisValue = -Infinity;

    const columnMetadata = dataWithAllDates.columnMetadata;
    for (
        let rowIndex = 0;
        rowIndex < Object.entries(dataWithAllDates.dataPoints).length;
        rowIndex++
    ) {
        const row = dataWithAllDates.dataPoints[rowIndex];
        const formattedDataPoint: {[key: string]: string | number | null} = {};

        if (row === null || row === undefined) {
            continue;
        }

        for (
            let cellIndex = 0;
            cellIndex < columnMetadata.length;
            cellIndex++
        ) {
            const cell = row[cellIndex];
            const metadata = columnMetadata[cellIndex];

            if (metadata === undefined) {
                continue;
            }

            if (
                metadata.dataType === EratoDataTypes.numeric &&
                cell !== undefined &&
                cell !== null &&
                cell.value !== null
            ) {
                const parsedData = parseFloat(`${cell.value}`);

                if (
                    secondaryAxisSelectedMetrics.includes(metadata.id) === true
                ) {
                    if (parsedData < minSecondaryAxisValue) {
                        minSecondaryAxisValue = parsedData;
                    }

                    if (parsedData > maxSecondaryAxisValue) {
                        maxSecondaryAxisValue = parsedData;
                    }
                } else {
                    if (parsedData < minValue) {
                        minValue = parsedData;
                    }

                    if (parsedData > maxValue) {
                        maxValue = parsedData;
                    }
                }
            }

            formattedDataPoint[metadata.displayName] = cell?.value ?? null;
        }

        chartData.push(formattedDataPoint);
    }

    const chartSeries: Array<ChartSeries> = [];

    for (let i = 0; i < columnMetadata.length; i++) {
        const metadata = columnMetadata[i];
        if (metadata === undefined) {
            continue;
        }
        if (metadata.columnType === EratoColumnTypes.dimension) {
            continue;
        }

        let yAxisId: YAxisId = "left";
        if (secondaryAxisSelectedMetrics.includes(metadata.id)) {
            yAxisId = "right";
        }

        chartSeries.push({
            name: metadata.displayName,
            color: graphColors[i % graphColors.length]!,
            isNameUpdated: false,
            label: `${metadata.displayName}`,
            yAxisId: yAxisId,
        });
    }

    return {
        chartData: chartData,
        chartSeries: chartSeries,
        dimensionName: dimension.displayName,
        maxYValue: maxValue,
        minYValue: minValue,
        minYValueSecondary: minSecondaryAxisValue,
        maxYValueSecondary: maxSecondaryAxisValue,
        title: `${chartSeries.map((series) => series.label).join(", ")} by ${dimension.displayName
            }`,
    };
}

export function transformComboChartData(
    rawData: HadesRawData,
    startDate: Iso8601DateTime,
    endDate: Iso8601DateTime,
    granularity: Granularity | null = Granularity.day,
    secondaryAxisSelectedMetrics: Array<Uuid>,
    chartSeriesDetails: Array<ComboChartSeriesDetails>,
): {
    chartData: Array<Record<string, string | number | null>>;
    chartSeries: Array<ComboChartSeries>;
    minYValue: number;
    maxYValue: number;
    dimensionName: string;
    minYValueSecondary: number;
    maxYValueSecondary: number;
    title: string;
} | null {
    let dataWithAllDates: HadesRawData = {
        columnMetadata: rawData.columnMetadata,
        dataPoints: [],
    };

    if (rawData.columnMetadata.length === 0) {
        return null;
    }

    if (rawData.dataPoints.length === 0) {
        return null;
    }

    // Validating whether column metadata count matches the number of values in each dataPoint
    const metadataLength = rawData.columnMetadata.length;
    for (const dataPoint of rawData.dataPoints) {
        if (metadataLength !== dataPoint.length) {
            return null;
        }
    }

    // TODO: Hardcoding a single dimension for charts, fix generalise later
    const dimension = rawData.columnMetadata.find(
        (metadata) => metadata.columnType === EratoColumnTypes.dimension,
    );
    if (dimension === undefined) {
        return null;
    }
    const dateColumnIndex: number | null =
        dimension.dataType === EratoDataTypes.date
            ? dimension.columnIndex
            : null;

    if (dateColumnIndex === null) {
        dataWithAllDates = rawData;
    } else {
        dataWithAllDates = generateHadesDataForAllDatesBasedOnGranularity(
            rawData,
            dateColumnIndex,
            startDate,
            endDate,
            granularity === null ? Granularity.day : granularity,
        );
    }

    const chartData: Array<Record<string, string | number | null>> = [];

    let minValue = Infinity;
    let maxValue = -Infinity;

    let minSecondaryAxisValue = Infinity;
    let maxSecondaryAxisValue = -Infinity;

    const columnMetadata = dataWithAllDates.columnMetadata;
    const secondaryAxisMetrics = chartSeriesDetails
        .filter((series) => series.yAxisId === "right")
        .map((series) => series.selectedMetrics)
        .flat();

    for (
        let rowIndex = 0;
        rowIndex < Object.entries(dataWithAllDates.dataPoints).length;
        rowIndex++
    ) {
        const row = dataWithAllDates.dataPoints[rowIndex];
        const formattedDataPoint: {[key: string]: string | number | null} = {};

        if (row === null || row === undefined) {
            continue;
        }

        for (
            let cellIndex = 0;
            cellIndex < columnMetadata.length;
            cellIndex++
        ) {
            const cell = row[cellIndex];
            const metadata = columnMetadata[cellIndex];

            if (metadata === undefined) {
                continue;
            }

            if (
                metadata.dataType === EratoDataTypes.numeric &&
                cell !== undefined &&
                cell !== null &&
                cell.value !== null
            ) {
                const parsedData = parseFloat(`${cell.value}`);

                if (secondaryAxisMetrics.includes(metadata.id) === true) {
                    if (parsedData < minSecondaryAxisValue) {
                        minSecondaryAxisValue = parsedData;
                    }

                    if (parsedData > maxSecondaryAxisValue) {
                        maxSecondaryAxisValue = parsedData;
                    }
                } else {
                    if (parsedData < minValue) {
                        minValue = parsedData;
                    }

                    if (parsedData > maxValue) {
                        maxValue = parsedData;
                    }
                }
            }

            formattedDataPoint[metadata.displayName] = cell?.value ?? null;
        }

        chartData.push(formattedDataPoint);
    }

    const chartSeries: Array<ComboChartSeries> = [];

    for (let i = 0; i < columnMetadata.length; i++) {
        const metadata = columnMetadata[i];
        if (
            metadata === undefined ||
            metadata.columnType === EratoColumnTypes.dimension
        ) {
            continue;
        }
        let yAxisId: YAxisId = "left";
        if (secondaryAxisMetrics.includes(metadata.id)) {
            yAxisId = "right";
        }

        const series = chartSeriesDetails.find((series) =>
            series.selectedMetrics.includes(metadata.id),
        );
        if (series === undefined || series.type === null) {
            continue;
        }

        chartSeries.push({
            name: metadata.displayName,
            color: graphColors[i % graphColors.length]!,
            isNameUpdated: false,
            label: `${metadata.displayName}`,
            yAxisId: yAxisId,
            chartType: series.type,
        });
    }

    return {
        chartData: chartData,
        chartSeries: chartSeries,
        dimensionName: dimension.displayName,
        maxYValue: maxValue,
        minYValue: minValue,
        minYValueSecondary: minSecondaryAxisValue,
        maxYValueSecondary: maxSecondaryAxisValue,
        title: `${chartSeries.map((series) => series.label).join(", ")} by ${dimension.displayName
            }`,
    };
}

export function transformStackedBarChartData(
    rawData: HadesRawData,
    startDate: Iso8601DateTime,
    endDate: Iso8601DateTime,
    granularity: Granularity | null = Granularity.day,
    slicer: string,
    // secondaryAxisSelectedMetrics: Array<Uuid>,
): {
    chartData: Array<Record<string, string | number | null>>;
    chartSeries: Array<ChartSeries>;
    minYValue: number;
    maxYValue: number;
    dimensionName: string;
    title: string;
    // minYValueSecondary: number | null;
    // maxYValueSecondary: number | null;
} | null {
    let dataWithAllDates: HadesRawData = {
        columnMetadata: rawData.columnMetadata,
        dataPoints: [],
    };

    if (rawData.columnMetadata.length === 0) {
        return null;
    }

    if (rawData.dataPoints.length === 0) {
        return null;
    }

    // Validating whether column metadata count matches the number of values in each dataPoint
    const metadataLength = rawData.columnMetadata.length;
    for (const dataPoint of rawData.dataPoints) {
        if (metadataLength !== dataPoint.length) {
            return null;
        }
    }

    // TODO: Hardcoding a single dimension for charts, fix generalise later
    let dimension1: HadesColumnMetadata | null = null;
    let dimension2: HadesColumnMetadata | null = null;

    for (const metadata of rawData.columnMetadata) {
        if (
            metadata.columnType === EratoColumnTypes.dimension &&
            dimension1 === null &&
            dimension2 === null
        ) {
            dimension1 = metadata;
            continue;
        }

        if (
            metadata.columnType === EratoColumnTypes.dimension &&
            dimension1 !== null &&
            dimension2 === null
        ) {
            dimension2 = metadata;
            break;
        }
    }

    if (dimension1 === null || dimension2 === null) {
        return null;
    }

    const slicerDimension =
        dimension1.id === slicer
            ? dimension1
            : dimension2.id === slicer
                ? dimension2
                : null;
    const xAxisDimension =
        dimension1.id !== slicer
            ? dimension1
            : dimension2.id !== slicer
                ? dimension2
                : null;

    if (slicerDimension === null || xAxisDimension === null) {
        return null;
    }

    const metric = rawData.columnMetadata.find(
        (metadata) => metadata.columnType === EratoColumnTypes.metric,
    );
    if (metric === undefined) {
        return null;
    }

    const dataWithFlattenedSlicer: HadesRawData = {
        columnMetadata: rawData.columnMetadata,
        dataPoints: [],
    };

    const groupedDataPointsByXAxisDimension: {
        [key: string]: {[key: string]: string};
    } = {};

    for (const dataPoint of rawData.dataPoints) {
        const xAxisDimensionValue = dataPoint[xAxisDimension.columnIndex];
        const slicerDimensionValue = dataPoint[slicerDimension.columnIndex];
        const metricValue = dataPoint[metric.columnIndex];

        if (
            xAxisDimensionValue === undefined ||
            xAxisDimensionValue.value === null ||
            slicerDimensionValue === undefined ||
            slicerDimensionValue.value === null ||
            metricValue === undefined ||
            metricValue.value === null
        ) {
            continue;
        }

        groupedDataPointsByXAxisDimension[xAxisDimensionValue.value] = {
            ...groupedDataPointsByXAxisDimension[xAxisDimensionValue.value],
            [slicerDimensionValue.value]: `${metricValue.value}`,
        };
    }

    const slicedColumnMetadata: Array<HadesColumnMetadata> = [
        {
            ...xAxisDimension,
            columnIndex: 0,
        },
    ];

    const sampleRow = Object.values(groupedDataPointsByXAxisDimension)[0];
    if (sampleRow === undefined) {
        return null;
    }

    const uniqueKeys = distinct(
        Object.values(groupedDataPointsByXAxisDimension)
            .map((value) => Object.keys(value))
            .flat(),
    );

    slicedColumnMetadata.push(
        ...uniqueKeys.map((row, index) => ({
            id: generateUuid(),
            columnIndex: index + 1,
            displayName: row,
            dataType: EratoDataTypes.numeric,
            columnType: EratoColumnTypes.metric,
        })),
    );

    dataWithFlattenedSlicer.columnMetadata = slicedColumnMetadata;

    let flattenedDataPoint: Array<{value: string}> = [];
    for (const [groupKey, groupValues] of Object.entries(
        groupedDataPointsByXAxisDimension,
    )) {
        flattenedDataPoint = [];
        for (const metadata of slicedColumnMetadata) {
            if (metadata.columnType === EratoColumnTypes.metric) {
                flattenedDataPoint[metadata.columnIndex] = {
                    value: groupValues[metadata.displayName] ?? null,
                };
            } else {
                flattenedDataPoint[metadata.columnIndex] = {
                    value: groupValues[metadata.displayName] ?? groupKey,
                };
            }
        }

        dataWithFlattenedSlicer.dataPoints.push(flattenedDataPoint);
    }

    const dateColumnIndex: number | undefined = slicedColumnMetadata.find(
        (metadata) => metadata.dataType === EratoDataTypes.date,
    )?.columnIndex;

    if (dateColumnIndex === undefined) {
        dataWithAllDates = dataWithFlattenedSlicer;
    } else {
        dataWithAllDates = generateHadesDataForAllDatesBasedOnGranularity(
            dataWithFlattenedSlicer,
            dateColumnIndex,
            startDate,
            endDate,
            granularity === null ? Granularity.day : granularity,
        );
    }

    const chartData: Array<Record<string, string | number | null>> = [];

    let minValue = Infinity;
    let maxValue = -Infinity;

    // let minSecondaryAxisValue = Infinity;
    // let maxSecondaryAxisValue = -Infinity;

    const columnMetadata = dataWithAllDates.columnMetadata;
    for (
        let rowIndex = 0;
        rowIndex < Object.entries(dataWithAllDates.dataPoints).length;
        rowIndex++
    ) {
        const row = dataWithAllDates.dataPoints[rowIndex];
        const formattedDataPoint: {[key: string]: string | number | null} = {};

        if (row === undefined) {
            continue;
        }

        for (
            let cellIndex = 0;
            cellIndex < columnMetadata.length;
            cellIndex++
        ) {
            const cell = row[cellIndex];
            const metadata = columnMetadata[cellIndex];

            if (metadata === undefined) {
                continue;
            }

            if (
                metadata.dataType === EratoDataTypes.numeric &&
                cell !== undefined &&
                cell !== null &&
                cell.value !== null
            ) {
                const parsedData = parseFloat(`${cell.value}`);
                // if (
                //     secondaryAxisSelectedMetrics.includes(metadata.id) === true
                // ) {
                //     if (parsedData < minSecondaryAxisValue) {
                //         minSecondaryAxisValue = parsedData;
                //     }

                //     if (parsedData > maxSecondaryAxisValue) {
                //         maxSecondaryAxisValue = parsedData;
                //     }
                // } else {
                if (parsedData < minValue) {
                    minValue = parsedData;
                }

                if (parsedData > maxValue) {
                    maxValue = parsedData;
                }
                // }
            }

            formattedDataPoint[metadata.displayName] = cell?.value ?? null;
        }

        chartData.push(formattedDataPoint);
    }

    const chartSeries: Array<ChartSeries> = [];

    for (let i = 0; i < columnMetadata.length; i++) {
        const metadata = columnMetadata[i];
        if (
            metadata === undefined ||
            metadata.columnType === EratoColumnTypes.dimension
        ) {
            continue;
        }
        const yAxisId: YAxisId = "left";
        // if (secondaryAxisSelectedMetrics.includes(metadata.id)) {
        //     yAxisId = "right";
        // }

        chartSeries.push({
            name: metadata.displayName,
            color: graphColors[i % graphColors.length]!,
            isNameUpdated: false,
            label: `${metric.displayName}`,
            yAxisId: yAxisId,
        });
    }

    return {
        chartData: chartData,
        chartSeries: chartSeries,
        dimensionName: xAxisDimension.displayName,
        maxYValue: maxValue,
        minYValue: minValue,
        title: `${metric.displayName} across ${slicerDimension.displayName} by ${xAxisDimension.displayName}`,
        // minYValueSecondary: minSecondaryAxisValue,
        // maxYValueSecondary: maxSecondaryAxisValue,
    };
}

export function transformPieChartData(
    rawData: HadesRawData,
): {pieChartData: EditableReportPieChartData; title: string} | null {
    if (rawData.columnMetadata.length === 0) {
        return null;
    }

    if (rawData.dataPoints.length === 0) {
        return null;
    }

    // Validating whether column metadata count matches the number of values in each dataPoint
    const metadataLength = rawData.columnMetadata.length;
    for (const dataPoint of rawData.dataPoints) {
        if (metadataLength !== dataPoint.length) {
            return null;
        }
    }

    const columnMetadata = rawData.columnMetadata;

    // TODO: Hardcoding a single metric and dimension for pie chart, generalise later
    const metric = columnMetadata.find(
        (metadata) => metadata.columnType === EratoColumnTypes.metric,
    );
    const dimension = columnMetadata.find(
        (metadata) => metadata.columnType === EratoColumnTypes.dimension,
    );

    if (metric === undefined || dimension === undefined) {
        return null;
    }

    const pieChartDataTransformed: Array<{
        name: string;
        key: string;
        value: number;
        color: string;
        label: string;
        [dimensionName: string]: string | number;
    }> = [];

    for (const [rowIndex, row] of rawData.dataPoints.entries()) {
        const dimensionValue = row[dimension.columnIndex]?.value;
        const metricValue = row[metric.columnIndex]?.value;
        const color = pieColors[rowIndex % pieColors.length];

        if (
            dimensionValue === null ||
            metricValue === null ||
            metric.dataType !== EratoDataTypes.numeric ||
            color === undefined
        ) {
            continue;
        }

        pieChartDataTransformed.push({
            key: dimension.displayName,
            name: `${dimensionValue}`,
            [dimension.displayName]: `${dimensionValue}`,
            value: parseFloat(`${metricValue}`),
            color: color,
            label: `${dimensionValue}`,
        });
    }

    return {
        pieChartData: pieChartDataTransformed,
        title: `${metric.displayName} by ${dimension.displayName}`,
    };
}

export function transformTableData(rawData: HadesRawData): {
    data: EditableReportTableData;
} | null {
    const columnsMetadata = rawData.columnMetadata;
    if (rawData.dataPoints.length === 0) {
        return null;
    }

    type TransformedTableRow = {[key: string]: string | number | null};

    const transformedTableData: Array<TransformedTableRow> = [];

    for (const row of rawData.dataPoints) {
        const transformedTableRow: TransformedTableRow = {};
        for (const [cellIndex, cell] of row.entries()) {
            const columnMetadata = columnsMetadata[cellIndex];
            if (columnMetadata === undefined) {
                continue;
            }

            if (columnMetadata.columnType === EratoColumnTypes.dimension) {
                if (
                    columnMetadata.dataType === EratoDataTypes.date &&
                    typeof cell.value === "string"
                ) {
                    transformedTableRow[columnMetadata.displayName] =
                        DateTime.fromISO(cell.value).toFormat("dd LLL yyyy");
                } else {
                    transformedTableRow[columnMetadata.displayName] =
                        cell.value;
                }
            }
        }

        for (const [cellIndex, cell] of row.entries()) {
            const columnMetadata = columnsMetadata[cellIndex];
            if (columnMetadata === undefined) {
                continue;
            }

            if (cell.value === null) {
                transformedTableRow[columnMetadata.displayName] = null;
            }

            if (
                columnMetadata.columnType === EratoColumnTypes.metric &&
                typeof cell.value === "number"
            ) {
                transformedTableRow[columnMetadata.displayName] =
                    Number.isInteger(cell.value) === true
                        ? cell.value
                        : parseFloat(cell.value.toFixed(2));
            }
        }

        transformedTableData.push(transformedTableRow);
    }

    return {
        data: transformedTableData,
    };
}

export function transformNumberTileData(
    rawData: HadesRawData,
): {value: number; title: string} | null {
    const firstDataPoint = rawData.dataPoints[0];
    if (
        rawData.columnMetadata.length === 0 ||
        rawData.dataPoints.length === 0 ||
        firstDataPoint?.length === 0 ||
        firstDataPoint === undefined ||
        firstDataPoint[0] === undefined ||
        rawData.columnMetadata[0]?.dataType !== EratoDataTypes.numeric ||
        typeof firstDataPoint[0].value !== "number"
    ) {
        return null;
    }

    return {
        value: firstDataPoint[0].value,
        title: rawData.columnMetadata[0].displayName,
    };
}

export function transformHadesColumnMetadataToColumnNameToDataTypeMap(
    columnMetadata: Array<HadesColumnMetadata>,
): FrontendColumnMetadata {
    const resolved: {[key: string]: keyof typeof EratoDataTypes} = {};

    for (const column of columnMetadata) {
        resolved[column.displayName] = column.dataType;
    }

    return resolved;
}

export function buildColumnAttributeIds(columnMetadata: Array<HadesColumnMetadata>): Record<string, string> {
    const result: Record<string, string> = {};
    for (const column of columnMetadata) {
        result[column.displayName] = column.id;
    }
    return result;
}
